import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import type { LocationRow, PlantRow } from "@garden/types/db";
import { supabase } from "../auth";
import { getPlant, updatePlant } from "../db/plants";
import { listLocations } from "../db/locations";
import { useCurrentGarden } from "../lib/useCurrentGarden";
import { PlantForm } from "../components/PlantForm";
import type { PlantCreateInput } from "../db/schemas";
import { parsePosition } from "../lib/geo";
import "./PlantPage.css";

export function PlantEdit() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [plant, setPlant] = useState<PlantRow | null>(null);
  const [locations, setLocations] = useState<LocationRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const { current: garden } = useCurrentGarden();

  useEffect(() => {
    if (!supabase || !id || !garden) return;
    let active = true;
    (async () => {
      try {
        const [p, locs] = await Promise.all([
          getPlant(supabase!, id),
          listLocations(supabase!, garden.id),
        ]);
        if (!active) return;
        setPlant(p);
        setLocations(locs);
      } catch (err) {
        if (!active) return;
        setError(err instanceof Error ? err.message : "Could not load plant.");
      }
    })();
    return () => {
      active = false;
    };
  }, [id, garden?.id]);

  if (error) return <div className="PlantPage__error">{error}</div>;
  if (!plant || !garden) return <div className="PlantPage__loading">Loading…</div>;

  const handleSubmit = async (input: PlantCreateInput) => {
    if (!supabase || !id) throw new Error("Not ready.");
    await updatePlant(supabase, id, input);
    navigate(`/plants/${id}`);
  };

  return (
    <div className="PlantPage">
      <div className="PlantPage__crumbs">
        <a href="#" onClick={(e) => { e.preventDefault(); navigate("/plants"); }}>Plants</a>
        <span>›</span>
        <a href="#" onClick={(e) => { e.preventDefault(); navigate(`/plants/${id}`); }}>
          {plant.common_name}
        </a>
        <span>›</span>
        <em>Edit</em>
      </div>
      <h1 className="PlantPage__title">Edit plant</h1>
      <PlantForm
        locations={locations}
        gardenId={garden.id}
        submitLabel="Save changes"
        initial={{
          common_name: plant.common_name,
          scientific_name: plant.scientific_name ?? "",
          cultivar: plant.cultivar ?? "",
          location_id: plant.location_id ?? "",
          planted_date: plant.planted_date ?? "",
          source_acquisition: plant.source_acquisition ?? "",
          status: plant.status,
          notes: plant.notes ?? "",
          symbol: plant.symbol,
          position: parsePosition(plant.position),
        }}
        onSubmit={handleSubmit}
        onCancel={() => navigate(`/plants/${id}`)}
        onLocationCreated={(loc) => setLocations((ls) => [loc, ...ls])}
      />
    </div>
  );
}
